import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { getTelegramStatus, linkTelegram, unlinkTelegram, testTelegram } from '../../services/api';
import { Button } from '../../components/ui/button';
import { CheckCircle, Warning, PaperPlaneTilt, LinkBreak, Bell } from '@phosphor-icons/react';
import { toast } from 'sonner';

const TelegramSettingsPage = () => {
  const { t } = useTranslation();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [linking, setLinking] = useState(false);

  const loadStatus = useCallback(async () => {
    try {
      const res = await getTelegramStatus();
      setStatus(res.data);
    } catch {
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadStatus(); }, [loadStatus]);

  const handleLink = async () => {
    setLinking(true);
    try {
      const res = await linkTelegram();
      toast.success(`Telegram linked! (${res.data.username || res.data.chat_id})`);
      loadStatus();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to link Telegram');
    } finally {
      setLinking(false);
    }
  };

  const handleUnlink = async () => {
    try {
      await unlinkTelegram();
      toast.success('Telegram unlinked');
      setStatus({ linked: false });
    } catch (err) {
      toast.error('Failed to unlink');
    }
  };

  const handleTest = async () => {
    try {
      await testTelegram();
      toast.success('Test notification sent! Check your Telegram.');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send test');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="telegram-settings-page">
      <div>
        <h1 className="text-2xl font-bold tracking-tight">Telegram Notifications</h1>
        <p className="text-muted-foreground mt-1">Receive real-time notifications via Telegram</p>
      </div>

      <div className="card-brutalist max-w-2xl">
        {status?.linked ? (
          <div className="space-y-6">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-emerald-100 flex items-center justify-center">
                <CheckCircle weight="fill" className="w-6 h-6 text-emerald-600" />
              </div>
              <div>
                <p className="font-bold text-emerald-800">Telegram Connected</p>
                <p className="text-sm text-muted-foreground">
                  {status.username ? `@${status.username}` : `Chat ID: ${status.chat_id}`}
                </p>
              </div>
            </div>

            <div className="bg-secondary/50 p-4 space-y-2">
              <p className="font-medium text-sm">You will receive notifications for:</p>
              <ul className="text-sm text-muted-foreground space-y-1">
                <li className="flex items-center gap-2"><Bell weight="duotone" className="w-4 h-4" /> New orders created</li>
                <li className="flex items-center gap-2"><Bell weight="duotone" className="w-4 h-4" /> Car status changes</li>
                <li className="flex items-center gap-2"><Bell weight="duotone" className="w-4 h-4" /> New document uploads</li>
              </ul>
            </div>

            <div className="flex gap-3">
              <Button onClick={handleTest} variant="outline" data-testid="telegram-test-btn">
                <PaperPlaneTilt className="w-4 h-4 mr-2" />
                Send Test
              </Button>
              <Button onClick={handleUnlink} variant="outline" className="text-destructive border-destructive/30" data-testid="telegram-unlink-btn">
                <LinkBreak className="w-4 h-4 mr-2" />
                Disconnect
              </Button>
            </div>
          </div>
        ) : (
          <div className="space-y-6">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-amber-100 flex items-center justify-center">
                <Warning weight="fill" className="w-6 h-6 text-amber-600" />
              </div>
              <div>
                <p className="font-bold">Telegram Not Connected</p>
                <p className="text-sm text-muted-foreground">Link your Telegram to receive notifications</p>
              </div>
            </div>

            <div className="bg-secondary/50 p-4 space-y-3">
              <p className="font-medium text-sm">How to connect:</p>
              <ol className="text-sm text-muted-foreground space-y-2 list-decimal list-inside">
                <li>Open Telegram and search for <a href="https://t.me/Management360bot" target="_blank" rel="noreferrer" className="text-primary font-medium hover:underline">@Management360bot</a></li>
                <li>Send the <code className="bg-secondary px-1.5 py-0.5 text-xs font-mono">/start</code> command to the bot</li>
                <li>Come back here and click <strong>"Connect Telegram"</strong></li>
              </ol>
            </div>

            <Button onClick={handleLink} disabled={linking} className="btn-primary" data-testid="telegram-link-btn">
              <PaperPlaneTilt className="w-4 h-4 mr-2" />
              {linking ? 'Connecting...' : 'Connect Telegram'}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default TelegramSettingsPage;
